// ./src/utils/api/PersonNoteService.ts
//
// PersonNote service — structured notes attached to a contact. OpResult-native.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

const PERSON_NOTE_FIELDS = `
  id
  personId
  accountId
  eventId
  title
  note
  isPinned
  status
  rights
  createdAt
  updatedAt
`;

const PERSON_NOTE_LIST_QUERY = gql`
  query PersonNoteList($filter: PersonNoteFilter, $params: PaginationAndSorting) {
    personNoteList(filter: $filter, params: $params) {
      code
      data { ${PERSON_NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_NOTE_CREATE_MUTATION = gql`
  mutation PersonNoteCreate($params: PersonNoteInput) {
    personNoteCreate(params: $params) {
      code
      data { ${PERSON_NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_NOTE_UPDATE_MUTATION = gql`
  mutation PersonNoteUpdate($where: PersonNoteWhereInput, $params: PersonNoteInput) {
    personNoteUpdate(where: $where, params: $params) {
      code
      data { ${PERSON_NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_NOTE_REMOVE_MUTATION = gql`
  mutation PersonNoteRemove($where: PersonNoteWhereInput) {
    personNoteRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface PersonNote {
  id?: string;
  personId?: string | null;
  accountId?: string;
  eventId?: string | null;
  title?: string | null;
  note?: string | null;
  isPinned?: boolean | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PersonNoteInput {
  personId?: string | null;
  eventId?: string | null;
  title?: string | null;
  note?: string | null;
  isPinned?: boolean | null;
}

export interface PersonNoteFilter {
  id?: string[] | null;
  personId?: string[] | null;
  eventId?: string[] | null;
}

export interface PersonNoteWhereInput {
  id?: string;
}

export class PersonNoteService {
  static async list(
    filter?: PersonNoteFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(PERSON_NOTE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async create(params: PersonNoteInput): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_NOTE_CREATE_MUTATION, { params });
  }

  static async update(where: PersonNoteWhereInput, params: PersonNoteInput): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_NOTE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_NOTE_REMOVE_MUTATION, { where: { id } });
  }
}
